import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { AnimatedNumber } from '../AnimatedNumber';
import { PointsAmount } from '../PointsAmount';
import { LinkGlyph } from './icons';
import { CrowdMeter, GraduationStat, MarketCapStat, money } from './parts';
import { CORRECT_CALL_POINTS, type CoinView } from './model';
import { useChartMotion } from './useChartMotion';
import { ResultTag } from './ResultTag';
import { useRailPreviewPosition } from './useRailPreviewPosition';
import {
  previewDotOffsets,
  previewDotScale,
  previewPosition,
  previewBet,
} from './previewModel';

function PriceScale({
  coin,
  wide = false,
}: {
  coin: CoinView;
  wide?: boolean;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(wide ? 380 : 229);
  const graduation = useRef<HTMLSpanElement>(null);
  const [pillWidth, setPillWidth] = useState(48);
  useLayoutEffect(() => {
    const node = graduation.current;
    if (!node) return;
    const measure = () => {
      setPillWidth(Number.parseFloat(getComputedStyle(node).width));
      if (track.current)
        setTrackWidth(Number.parseFloat(getComputedStyle(track.current).width));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    if (track.current) observer.observe(track.current);
    return () => observer.disconnect();
  }, []);
  const position = previewPosition(coin);
  const edge = Math.max(0, trackWidth / 2 - 17);
  // Keep the marker inside the appropriate dotted zone, clear of the centre label.
  const gap = Math.min(edge, pillWidth / 2 + 6);
  const offset =
    position === null || position === 0
      ? 0
      : Math.sign(position) *
        (gap + Math.abs(position) * Math.max(0, edge - gap));
  const motion = useChartMotion(
    coin.id,
    position === null ? null : [{ x: offset, y: 0 }],
  );
  const cursor = motion ? Math.max(-edge, Math.min(edge, motion[0].x)) : null;
  const dots = previewDotOffsets(pillWidth, edge, wide ? 14 : 10);
  return (
    <div
      ref={track}
      className="preview-scale"
      role="img"
      aria-label={
        'Market cap ' +
        money(coin.marketCap) +
        ', at graduation ' +
        money(coin.graduationCap) +
        (position === null
          ? '. Comparison unavailable.'
          : position < 0
            ? '. Below graduation.'
            : position > 0
              ? '. Above graduation.'
              : '. At graduation.')
      }
    >
      <div className="preview-scale-line" aria-hidden="true">
        <span className="preview-dots">
          {dots[0].map((dot, i) => (
            <img
              key={i}
              src="/images/rail-dot-die.svg"
              width={4}
              height={4}
              style={{ transform: `scale(${previewDotScale(dot, cursor)})` }}
              alt=""
            />
          ))}
        </span>
        <span ref={graduation} className="preview-graduation">
          <AnimatedNumber value={money(coin.graduationCap)} />
        </span>
        <span className="preview-dots">
          {dots[1].map((dot, i) => (
            <img
              key={i}
              src="/images/rail-dot-run.svg"
              width={4}
              height={4}
              style={{ transform: `scale(${previewDotScale(dot, cursor)})` }}
              alt=""
            />
          ))}
        </span>
      </div>
      {position === null ? (
        <span className="preview-scale-empty">No USD comparison</span>
      ) : (
        <>
          <span
            className={'preview-price ' + (position >= 0 ? 'is-up' : 'is-down')}
            style={
              {
                '--price-offset': `${cursor ?? 0}px`,
                '--price-edge': `${edge}px`,
              } as CSSProperties
            }
            aria-hidden="true"
          >
            <AnimatedNumber value={money(coin.marketCap)} />
          </span>
          <span
            className="preview-cursor"
            style={{ left: `calc(50% + ${cursor}px)` }}
            aria-hidden="true"
          >
            <img
              className="preview-pointer"
              src="/images/rail-price-pointer.svg"
              alt=""
              width={13}
              height={11}
            />
          </span>
        </>
      )}
    </div>
  );
}

export function CoinPreview({
  id,
  coin,
  now,
  side,
}: {
  id: string;
  coin: CoinView;
  now: number;
  side: 'left' | 'right';
}) {
  const preview = useRailPreviewPosition(side);
  const bet = previewBet(coin);
  return (
    <div
      ref={preview}
      id={id}
      className={'coin-preview is-' + side}
      role="tooltip"
      data-preview-state={bet?.result ?? (bet ? 'bet' : 'default')}
    >
      <CoinSummary coin={coin} now={now} />
    </div>
  );
}

// Rail previews and Explore share the chart, crowd, timer and personal result.
export function CoinSummary({
  coin,
  now,
  header,
  layout = 'rail',
}: {
  coin: CoinView;
  now: number;
  header?: ReactNode;
  layout?: 'rail' | 'explore';
}) {
  const bet = previewBet(coin);
  return (
    <div
      className={
        'coin-summary' + (layout === 'rail' ? ' coin-preview-body' : '')
      }
    >
      {header ?? (
        <div className="preview-head">
          <div className="preview-title">
            <h3 title={coin.name}>{coin.name}</h3>
            <p className="preview-pair">
              <span>${coin.symbol}</span>
              {coin.pairSymbol && (
                <>
                  <LinkGlyph />
                  <span className="preview-pair-name">{coin.pairSymbol}</span>
                </>
              )}
            </p>
          </div>
        </div>
      )}
      <hr />
      <PriceScale coin={coin} wide={layout === 'explore'} />
      <div className="preview-stats">
        <MarketCapStat coin={coin} />
        <GraduationStat coin={coin} />
      </div>
      <hr />
      <CrowdMeter coin={coin} />
      <div className={'preview-footer' + (bet ? ' has-bet' : '')}>
        <ResultTag coin={coin} now={now} layout={layout} />
        {bet && (
          <>
            {layout === 'rail' && <hr />}
            <div
              className={
                'preview-bet' + (bet.result ? ' is-' + bet.result : '')
              }
            >
              <img
                className="preview-bet-frame"
                src={'/images/' + layout + '-bet-frame.svg'}
                width={layout === 'explore' ? 188 : 230}
                height={46.5}
                alt=""
                aria-hidden="true"
              />
              {bet.result && (
                <span className="preview-bet-outline" aria-hidden="true" />
              )}
              <div className="preview-bet-content">
                <div className="preview-bet-details">
                  <b className="preview-bet-amount">
                    <PointsAmount
                      value={
                        coin.lockedOdds?.amount ??
                        (coin.simulated && !coin.id.startsWith('game-')
                          ? CORRECT_CALL_POINTS
                          : '—')
                      }
                    />
                  </b>
                  <span
                    className={
                      'preview-bet-choice is-' +
                      (bet.choice === 'RUN' ? 'up' : 'down')
                    }
                  >
                    {bet.choice === 'RUN' ? 'RUN!' : 'DIE!'}
                  </span>
                  {bet.multiplier !== null && (
                    <span
                      className="preview-bet-odds"
                      aria-label={
                        coin.simulated && !coin.lockedOdds
                          ? 'Current crowd multiplier'
                          : 'Your fixed coefficient'
                      }
                      title={
                        bet.multiplier.toFixed(
                          coin.simulated && !coin.lockedOdds ? 1 : 2,
                        ) + 'x'
                      }
                    >
                      <AnimatedNumber
                        value={
                          bet.multiplier.toFixed(
                            coin.simulated && !coin.lockedOdds ? 1 : 2,
                          ) + 'x'
                        }
                      />
                    </span>
                  )}
                </div>
              </div>
              <div className="preview-bet-caption">
                <span className="preview-bet-label">Your bet</span>
                {bet.result && (
                  <span
                    className={'preview-outcome is-' + bet.result}
                    aria-label={
                      bet.result === 'win' ? 'Your bet won' : 'Your bet lost'
                    }
                  >
                    {bet.result === 'win' ? 'WIN' : 'LOSE'}
                  </span>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {coin.simulated && (
        <span className="sr-only">Simulated token and bets.</span>
      )}
      {coin.choice && (
        <span className="sr-only">
          Your call: {coin.choice === 'RUN' ? 'RUN!' : 'DIE!'}
        </span>
      )}
    </div>
  );
}
